import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GetAppIcon from "@mui/icons-material/GetApp";
import HistoryIcon from "@mui/icons-material/History";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import PlayCircleIcon from "@mui/icons-material/PlayCircleFilled";
import PanToolIcon from "@mui/icons-material/PanTool";
import LoopIcon from "@mui/icons-material/Loop";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

import { ActionCourse } from "src/shared/api-types/certificate";

import { CourseCertificate } from "src/api/compliance";
import { captureHandledException } from "src/services/sentry";

import Button from "../../components/Button";
import useContextUserCrew from "../../hooks/useContextUserCrew";
import { useCertificateActions } from "../../context/CertificateActions";
import { useShoppingCart } from "../../context/ShoppingCartContext";
import { useConfirm } from "../../context/ConfirmContext";
import { openDownloadWindow, openExportWindow } from "../../api/";
import { getTrainingByCourseId, addUserToTrainingCourse, getUserToTrainingCourse } from "../../api/training.deprecated";
import { Certificate } from "../../types/certificate";
import { CertificateStatusType } from "../../services/certificateStatus";
import { BundleWithActions } from "../../features/compliance/bundles";
import { useMoodleCourseOpener } from "../Moodle/useMoodleCourseOpener";
import { PaymentRequiredError } from "../../api/request";
import useFeatureFlag, { FEATURE_FLAGS } from "../../hooks/useFeatureFlag";

interface ActionsProps {
  courseCertificate?: CourseCertificate;
  bundle?: BundleWithActions;
}

const getLatestPlayableRefresherCourse = (courseCertificate?: CourseCertificate): ActionCourse | null => {
  const latestPlayableRefresher = courseCertificate?.refreshers
    ?.filter((refresher) => !refresher.inactive && refresher.actions?.playableCourse)
    .sort((a, b) => b.id - a.id)[0];

  return latestPlayableRefresher?.actions?.playableCourse ?? null;
};

const getVisiblePlayableCourse = (
  canShowGreenCourseActions: boolean,
  courseCertificate: CourseCertificate | undefined,
  fallbackPlayableCourse: ActionCourse | null,
): ActionCourse | null => {
  if (!canShowGreenCourseActions) {
    return null;
  }

  const rowPlayableCourse = courseCertificate?.actions?.playableCourse ?? fallbackPlayableCourse;
  return rowPlayableCourse;
};

const Actions = ({ courseCertificate, bundle }: ActionsProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { userId, crewId } = useContextUserCrew();
  const onNotFound = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });
  const history = useHistory();
  const actions = useCertificateActions();
  const shoppingCart = useShoppingCart();
  const dialog = useConfirm();
  const openMoodleCourse = useMoodleCourseOpener();
  const showPlayButtonForGreenCourse = useFeatureFlag(FEATURE_FLAGS.ENABLE_PLAY_BUTTON_FOR_GREEN_COURSE);
  const itemActions = courseCertificate?.actions || bundle?.actions;
  const certificate = courseCertificate?.actions?.downloadableCertificate || courseCertificate?.certificate;
  const isCustom = courseCertificate?.custom || courseCertificate?.type === "custom";
  const latestPlayableRefresherCourse = getLatestPlayableRefresherCourse(courseCertificate);
  const canShowGreenCourseActions = !courseCertificate?.hideGreenCoursePlayAction || showPlayButtonForGreenCourse;
  const playableCourse = getVisiblePlayableCourse(
    canShowGreenCourseActions,
    courseCertificate,
    latestPlayableRefresherCourse,
  );
  const showPurchasedStatus = itemActions?.isPurchased && canShowGreenCourseActions;

  const openCourseClick = useCallback(
    async (course: ActionCourse | null) => {
      if (!course) return;
      const openCourseId = course.trainingHierarchy?.highestInTrainingHierarchyCourseId || course.id;
      const openCourseName = course.trainingHierarchy?.highestInTrainingHierarchyCourseName || course.name;

      if (course.type === "training") {
        try {
          const training = await getTrainingByCourseId(openCourseId);
          const userToTraining = await getUserToTrainingCourse(training.id, userId, crewId);

          if (!userToTraining) {
            await addUserToTrainingCourse(training.id, userId, crewId, openCourseId, "user", "");
          }

          history.push(`/training-users/user/details/${training.id}/${userId}/${openCourseId}`);
        } catch (error) {
          console.error("Error handling training course:", error);
          captureHandledException(error, {
            component: "CertificateActions",
            action: "openTrainingCourse",
            courseId: openCourseId,
            userId,
            crewId,
          });
          dialog.notification(t("certificate.error"), t("certificate.error_training_course"));
        }
      } else {
        try {
          await openMoodleCourse(openCourseId, userId, openCourseName);
        } catch (error) {
          console.error("Error handling regular course:", error);
          captureHandledException(error, {
            component: "CertificateActions",
            action: "openMoodleCourse",
            courseId: openCourseId,
            userId,
            crewId,
          });
          // Don't show dialog for payment required errors - user is already navigated to payment page
          if (!(error instanceof PaymentRequiredError)) {
            dialog.notification(t("certificate.error"), t("certificate.error_processing_course"));
          }
        }
      }
    },
    [userId, crewId, history, dialog],
  );

  const getLatestRefresher = () => {
    const activeRefreshers = (courseCertificate?.refreshers?.filter((r) => !r.inactive) || [])
      .map((r) => ({
        ...r,
        refreshers: undefined,
      }))
      .sort((a, b) => b.id - a.id);

    return activeRefreshers[0];
  };

  const getActCertificateId = (): number | string | undefined => {
    if (courseCertificate?.type === "custom") {
      return `cc_${courseCertificate.id}`;
    }

    if (courseCertificate?.actions?.downloadableCertificate) {
      const latestRefresher = getLatestRefresher();
      if (latestRefresher) {
        return latestRefresher.id;
      }
    }

    return courseCertificate?.id;
  };

  // If no actions computed (backwards compatibility or missing data), return minimal UI
  if (!itemActions) {
    return (
      <div style={{ display: "flex", justifyContent: "start" }}>
        {actions?.showHistory && certificate?.status !== CertificateStatusType.MISSING && (
          <Button
            title={t("certificate.show_history")}
            icon={<HistoryIcon />}
            onClick={() => actions.showHistory(courseCertificate)}
          />
        )}
      </div>
    );
  }

  if (bundle) {
    return (
      <>
        {itemActions.purchasableCourse && (
          <Button
            title={t("certificate.add_to_cart_bundle", {
              id: itemActions.purchasableCourse.id,
              name: itemActions.purchasableCourse.name,
              price: itemActions.purchasableCourse.price ? `, ${itemActions.purchasableCourse.price}€/CHF` : "",
              interpolation: { escapeValue: false },
            })}
            icon={<AddShoppingCartIcon />}
            onClick={() => {
              shoppingCart.actions.add({ ...itemActions.purchasableCourse!, isBundle: true } as any, userId);
            }}
          />
        )}
        {itemActions.isPurchased && (
          <Button title={t("certificate.subscribed_to_bundle")} icon={<AssignmentTurnedInIcon />} />
        )}
      </>
    );
  }

  if (!courseCertificate) {
    return null;
  }

  // Do not show older grouped courses for purchase
  const allowGroupedCoursePurchase = courseCertificate.group
    ? itemActions?.purchasableCourse && itemActions.purchasableCourse.id >= courseCertificate.id
    : true;
  const purchasableCourse = itemActions.purchasableCourse;
  const showPurchaseAction = !!purchasableCourse && !!allowGroupedCoursePurchase && canShowGreenCourseActions;
  const latestRefresher = getLatestRefresher();
  return (
    <div style={{ display: "flex", justifyContent: "start" }}>
      {!itemActions.downloadableCertificate || !latestRefresher
        ? itemActions.canUploadCertificate && (
            <Button
              title={
                itemActions.isReupload
                  ? t("certificate.update_certificate_cloud")
                  : t("certificate.upload_certificate_cloud")
              }
              icon={<CloudUploadIcon sx={{ opacity: itemActions.isReupload ? 0.4 : 1 }} />}
              onClick={() => history.push(`/certificates/${crewId}/members/${userId}/upload/${getActCertificateId()}`)}
            />
          )
        : latestRefresher?.actions?.canUploadCertificate && (
            <Button
              title={
                latestRefresher.actions.isReupload
                  ? t("certificate.update_certificate_cloud")
                  : t("certificate.upload_certificate_cloud")
              }
              icon={<CloudUploadIcon sx={{ opacity: latestRefresher.actions.isReupload ? 0.4 : 1 }} />}
              onClick={() => history.push(`/certificates/${crewId}/members/${userId}/upload/${getActCertificateId()}`)}
            />
          )}

      {itemActions.downloadableCertificate && (
        <>
          {itemActions.downloadableCertificate.certificateFile && (
            <Button
              title={t("certificate.download_certificate")}
              icon={<GetAppIcon />}
              onClick={() => openDownloadWindow(itemActions.downloadableCertificate!, userId, isCustom, onNotFound)}
            />
          )}
          {!itemActions.downloadableCertificate.certificateFile && (
            <Button
              title={
                courseCertificate.trainingHierarchy?.passedByCourseName
                  ? t("certificate.download_certificate_passed_by", {
                      courseName: courseCertificate.trainingHierarchy.passedByCourseName,
                    })
                  : t("certificate.download_certificate")
              }
              icon={<GetAppIcon />}
              onClick={() =>
                openExportWindow(itemActions.downloadableCertificate?.id || courseCertificate.id, userId, onNotFound)
              }
            />
          )}
        </>
      )}

      {showPurchaseAction && (
        <Button
          title={
            purchasableCourse.trainingHierarchy?.highestInTrainingHierarchyCourseId
              ? t("certificate.add_to_cart_highest_hierarchy")
              : t("certificate.add_to_cart_course", {
                  id: purchasableCourse.id,
                  name: purchasableCourse.name,
                  price: purchasableCourse.price ? `, ${purchasableCourse.price}€/CHF` : "",
                  interpolation: { escapeValue: false },
                })
          }
          icon={<AddShoppingCartIcon />}
          onClick={() => {
            const courseToAdd = purchasableCourse;
            const finalCourse = courseToAdd.trainingHierarchy?.highestInTrainingHierarchyCourseId
              ? {
                  ...courseToAdd,
                  id: courseToAdd.trainingHierarchy.highestInTrainingHierarchyCourseId,
                  name: courseToAdd.trainingHierarchy.highestInTrainingHierarchyCourseName || courseToAdd.name,
                }
              : courseToAdd;
            shoppingCart.actions.add({ ...finalCourse, isBundle: false } as any, userId);
          }}
        />
      )}

      {showPurchasedStatus && (
        <Button
          title={t(playableCourse ? "certificate.course_bought_id" : "certificate.course_bought", {
            id: playableCourse?.id,
          })}
          icon={<AssignmentTurnedInIcon />}
          onClick={() => {}}
        />
      )}

      {playableCourse && (
        <Button
          title={
            playableCourse.trainingHierarchy?.highestInTrainingHierarchyCourseId
              ? t("certificate.go_to_highest_course")
              : t("certificate.go_to_course")
          }
          icon={<PlayCircleIcon />}
          onClick={() => openCourseClick(playableCourse)}
        />
      )}

      <Button
        title={t("certificate.show_history")}
        icon={<HistoryIcon sx={{ opacity: itemActions.hasHistory ? 1 : 0.4 }} />}
        onClick={() => actions?.showHistory?.(courseCertificate)}
      />

      {itemActions.noNewCourseAvailable && certificate?.status !== CertificateStatusType.OK && (
        <Button title={t("certificate.no_new_course_available")} icon={<PanToolIcon />} onClick={() => {}} />
      )}

      {itemActions.isPendingApproval && <Button title={t("certificate.waiting_for_approval")} icon={<LoopIcon />} />}
    </div>
  );
};

// Simplified component for export/download actions only
// Used in contexts where only download/export is needed (e.g., all-user-certificates page)
export const ExportActions = ({
  certificate,
  userId,
  isCustom,
}: {
  certificate: Certificate;
  userId: number;
  isCustom: boolean;
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onNotFound = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });
  const downloadableCertificate = certificate.actions?.downloadableCertificate;
  if (!downloadableCertificate) return null;

  if (!isCustom) {
    // @ts-ignore: This is a hack for downloading certificate to not overwrite passed isCustom value into openDownloadWindow function
    downloadableCertificate.type = "";
  }

  return (
    <div style={{ display: "flex", justifyContent: "start" }}>
      {downloadableCertificate.certificateFile ? (
        <Button
          title={t("certificate.download_certificate")}
          icon={<GetAppIcon />}
          onClick={() => openDownloadWindow(downloadableCertificate, userId, isCustom, onNotFound)}
        />
      ) : (
        <Button
          title={t("certificate.download_certificate")}
          icon={<GetAppIcon />}
          onClick={() => openExportWindow(certificate.latestRefresherId || certificate.id, userId, onNotFound)}
        />
      )}
    </div>
  );
};

export default Actions;
